import { AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@reflex/ui/components/ui/button';
import { Badge } from '@reflex/ui/components/ui/badge';
import { formatPluginName } from '@reflex/ui/lib/format';
import { useAuth, useOrgRoutes } from '@reflex/ui/client/context';
import { ROUTES } from '@reflex/shared/routes';
import { canOpenAdminConsole } from '../../hrefEntitlement.js';
import type { PluginDisabledPayload } from '../../../shared/types.js';

const SOURCE_LABEL: Record<PluginDisabledPayload['source'], string> = {
  validation: 'Validation',
  disabled_dependencies: 'Disabled dependency',
  migration: 'Migration',
  register: 'Registration',
  on_ready: 'Lifecycle hook',
  startup_health: 'Startup health',
  liveness_health: 'Liveness health',
  runtime: 'Runtime',
  unknown: 'Unknown',
};

const SOURCE_HINT: Record<PluginDisabledPayload['source'], string> = {
  validation:
    'A required secret or peer plugin dependency is missing. Configure it in Admin → Secrets (or install the missing plugin) and restart the server.',
  disabled_dependencies:
    "Another plugin this one depends on is itself disabled. Fix the named peer's failure first; this plugin will register again on the next restart once its dependency is back.",
  migration:
    'A plugin database migration failed. Check the server logs around boot for the underlying Postgres error, fix the migration or schema, and restart the server.',
  register:
    'The plugin threw while wiring its routes, provides() hook, or agent provider factory. Check the server logs for the stack trace and restart after the fix.',
  on_ready:
    'A Fastify lifecycle hook (preReady / onReady / onClose) threw. The plugin loaded but is now in a degraded state. Restart the server after fixing the underlying cause.',
  startup_health:
    'The plugin failed its boot-time health check. Verify external dependencies (Runloop, GitHub, etc.) are reachable and that all required service-tier secrets are set.',
  liveness_health:
    'The plugin reported unhealthy after boot. Check the dependency it relies on, then restart.',
  runtime: 'The plugin disabled itself during normal operation. Check the server logs for details.',
  unknown:
    'The host disabled this plugin but did not categorize the failure. Check the server logs around boot for context.',
};

export interface PluginDisabledRendererProps {
  payload: PluginDisabledPayload;
}

/**
 * Renders a notification announcing that a plugin is currently disabled.
 *
 * The body depends on who is reading it, because the two audiences have
 * different next steps:
 *
 * - **Instance operators** (anyone `canOpenAdminConsole` admits) get the diagnosis: the
 *   structured failure source, the raw reason string from
 *   `PluginStatusRegistry`, and a deep link into the admin plugins page
 *   where the fix lives.
 * - **Everyone else** gets what the failure means for them — the
 *   integration is unavailable in this workspace — and an action they can
 *   actually take. They see no server instructions ("restart the server"
 *   is not advice a tenant can follow), no raw reason string, and no link
 *   to a console they cannot open.
 *
 * The operator deep link points at the per-plugin admin detail page
 * (`/admin/plugins/<name>`) — the host's admin routes are mounted at the
 * top level under `<AuthedShell>` in `App.tsx`, **not** under
 * `orgs/:orgSlug`, so the link must be the bare admin path (an org-prefixed
 * `/orgs/<slug>/admin/...` URL has no matching route and falls through to
 * the org's 404 page).
 *
 * The branch asks about the **viewer**, never about the deployment, and
 * that is the whole point: there is no deploy-time flag that answers this
 * question. A dedicated single-tenant instance still carries Runloop's own
 * operators, who need the diagnosis; it can carry more than one
 * organization, so "who is on this box" is not a constant; and an admin
 * demoted five minutes ago is reading a row that was correctly addressed
 * to them when it was written. `canOpenAdminConsole` is right in every one
 * of those cases because it evaluates the same predicate the console's own
 * guard does, at the moment of render.
 *
 * The heading above the body is branched the same way and in one shared
 * place — see `notificationHeadingFor`. The server-authored title carries
 * the plugin name and the subtitle carries the raw failure reason, so
 * leaving those unbranched would hand a non-operator the diagnosis this
 * body exists to withhold.
 */
export function PluginDisabledRenderer({ payload }: PluginDisabledRendererProps) {
  const auth = useAuth();
  const label = formatPluginName(payload.pluginName);

  if (!canOpenAdminConsole(auth)) {
    return <PluginUnavailableBody label={label} />;
  }

  return (
    <div className="space-y-3" data-testid="notification-renderer-plugin-disabled">
      {/* Wraps on narrow viewports — a non-wrapping chip row inflates the
          ScrollArea's min-content width and pushes the whole detail panel
          past the phone viewport. */}
      <div className="flex flex-wrap items-center gap-2">
        <AlertTriangle className="icon-sm text-destructive" aria-hidden="true" />
        <Badge variant="destructive">Plugin disabled</Badge>
        <Badge variant="outline" data-testid="plugin-disabled-source">
          {SOURCE_LABEL[payload.source]}
        </Badge>
        <code
          className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs"
          data-testid="plugin-disabled-name"
        >
          {payload.pluginName}
        </code>
      </div>
      <p className="text-sm text-muted-foreground">{SOURCE_HINT[payload.source]}</p>
      <pre
        className="max-h-48 overflow-auto whitespace-pre-wrap rounded-md border bg-muted/40 p-3 font-mono text-xs"
        data-testid="plugin-disabled-reason"
      >
        {payload.reason}
      </pre>
      <Button asChild variant="outline" size="sm">
        <Link to={ROUTES.admin.plugin(payload.pluginName)} aria-label="Open plugin admin">
          Open plugin admin
        </Link>
      </Button>
    </div>
  );
}

/**
 * Non-operator body. Names the consequence in the reader's own terms and
 * offers the one page they can open — the workspace's integrations list,
 * which is org-scoped and reachable by every member.
 */
function PluginUnavailableBody({ label }: { label: string }) {
  const orgRoutes = useOrgRoutes();
  // `useOrgRoutes` falls back to the slug `'_'` when the viewer has no
  // active membership, which builds a URL with no matching route. This is
  // the only action this body offers, so a dead one is worse than none.
  const hasOrgRoute = orgRoutes.orgSlug !== '_';
  return (
    <div className="space-y-3" data-testid="notification-renderer-plugin-unavailable">
      <div className="flex flex-wrap items-center gap-2">
        <AlertTriangle className="icon-sm text-destructive" aria-hidden="true" />
        <Badge variant="destructive">Unavailable</Badge>
        <span className="text-sm font-medium" data-testid="plugin-unavailable-name">
          {label}
        </span>
      </div>
      {/* No "your administrators have been notified" — this component cannot
          know whether anyone who can act on it will read it, and a
          reassurance we cannot keep is worse than none. */}
      <p className="text-sm text-muted-foreground">
        {label} is unavailable in this workspace. Anything that depends on it — sign-in, lookups,
        and automations — will fail until it is back.
      </p>
      {hasOrgRoute && (
        <>
          <p className="text-sm text-muted-foreground">
            Check your integrations to see what else is connected, and use a different one in the
            meantime.
          </p>
          <Button asChild variant="outline" size="sm">
            <Link to={orgRoutes.integrations} aria-label="View integrations">
              View integrations
            </Link>
          </Button>
        </>
      )}
    </div>
  );
}
